/**
 * The bout bot's slides (duel v3 P1.5; the slide's rules: systems/duelSlide.ts). Kept out of systems/duelBot.ts: it watches the
 * bout vs a stand-in and sends the stand-in's slide commands itself, by style and level:
 * - evade: the foe winds up a heavy move (break or power) in reach, or dives at it -> a slide sideways (under the dive);
 * - corner: backed to the ring's edge with the foe close -> a slide along the edge, toward the ring's middle;
 * - gap-close: 3.5-6.5 m off -> a slide in (it stops short) with a slide strike in the end window, the pushy most.
 * Its draws come from a hash of the world tick and the bot's id, not the World's `Rng` (the bot brain's draws stay as they were).
 */
import { DUEL_SLIDE } from '../data/slide';
import { SKILLS } from '../data/skills';
import { hash2 } from '../rng';
import type { Entity } from '../world';
import { duelAirOf } from './duelAir';
import { duelMatchOf } from './duelMatch';
import { botSeatOf } from './botSeat'; // STUDIO-BOTDUEL: a bot-vs-bot seat (systems/botBout.ts)
import { duelMoveOf } from './duelMove';
import { duelRecoveryOf } from './duelRecovery';
import { slideOf } from './slide';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelBotSlideSystem>();
export const duelBotSlideOf = (w: object): DuelBotSlideSystem | null => SYSTEMS.get(w) ?? null;

/** Seconds between the bot's slide thoughts. */
const THINK = 0.2;
/** พลังกาย the bot keeps beyond a slide's cost (for a step after it). */
const RESERVE = 25;

export type BotSlideWhy = 'evade' | 'corner' | 'gap';

export class DuelBotSlideSystem implements WorldSystem {
  readonly name = 'duelBotSlide';
  /** Off: the bot slides only when told (captures that drive the bot by hand). */
  off = false;
  private nextT = 0;
  private n = 0;
  /** The bot's latest slide and why (tests, the picture). */
  latest: { why: BotSlideWhy; at: number } | null = null;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  step(_dt: number): void {
    const w = this.h.world;
    const st = botSeatOf(w) ?? duelMatchOf(w)?.current;
    if (this.off || !st || st.pvp || st.phase !== 'fight' || st.paused || !w.pvpBout?.fight) return;
    if (w.time < this.nextT) return;
    this.nextT = w.time + THINK;
    const bot = w.get(st.foeId);
    const foe = w.get(st.meId);
    if (!bot || !foe || bot.dead || foe.dead) return;
    if (!this.free(bot)) return;
    const rnd = () => hash2(Math.round(w.time * 60), bot.id * 977 + this.n++);
    const lv = st.level;
    const style = st.style;
    const rx = foe.x - bot.x;
    const ry = foe.y - bot.y;
    const dist = Math.hypot(rx, ry) || 1e-6;
    const ux = rx / dist;
    const uy = ry / dist;
    // The two sideways ways; the one toward the ring's middle first.
    const toMid = (bot.x - st.ring.x) * -uy + (bot.y - st.ring.y) * ux;
    const side: [number, number] = toMid <= 0 ? [-uy, ux] : [uy, -ux];

    // Evade a heavy wind-up in reach, or a dive coming down on it.
    const dive = duelAirOf(w)?.dive(foe.id);
    const heavy = foe.cast && ['break', 'power'].includes(SKILLS[foe.cast.skillId]?.kind ?? '') && dist < 4;
    const diving = dive && Math.hypot(dive.x - bot.x, dive.y - bot.y) < 2.2;
    if ((heavy || diving) && rnd() < style.evade * (0.35 + 0.1 * lv)) return this.slide(bot, side, 'evade');

    // Backed to the edge with the foe close: out along the edge, toward the middle.
    const fromMid = Math.hypot(bot.x - st.ring.x, bot.y - st.ring.y);
    if (fromMid > st.ring.r - 1.6 && dist < 3 && rnd() < 0.2 + 0.06 * lv) return this.slide(bot, side, 'corner');

    // In from range, with a slide strike.
    if (dist >= 3.5 && dist <= 6.5 && rnd() < style.aggression * (0.04 + 0.015 * lv)) {
      this.slide(bot, [ux, uy], 'gap');
      if (slideOf(bot)) w.apply({ t: 'attack' }, bot.id);
    }
  }

  /** On the ground, not hurt, casting, stepping, sliding or in a stop, with the พลังกาย for it. */
  private free(e: Entity): boolean {
    const w = this.h.world;
    if (e.z > 0 || e.stunT > 0 || e.cast || e.dashT > 0 || e.spinT > 0 || e.swingT > 0 || slideOf(e)) return false;
    if ((duelRecoveryOf(w)?.left(e.id) ?? 0) > 0 || (duelMoveOf(w)?.lockLeft(e.id) ?? 0) > 0) return false;
    return e.stamina >= DUEL_SLIDE.stamina * DUEL_SLIDE.spamMult + RESERVE;
  }

  private slide(bot: Entity, [dx, dy]: [number, number], why: BotSlideWhy): void {
    const w = this.h.world;
    w.apply({ t: 'slide', dx, dy }, bot.id);
    if (slideOf(bot)) this.latest = { why, at: w.time };
  }
}
